import { ref, computed, watch } from "vue";
import { ProductIds } from "@/config/constants";
import { useStore } from "vuex";
import type { TimelineMilestone } from "@/models/applicationStatus";
import useApplicationData from "@/composables/useApplicationData";

export default function useApplicationStatus() {
  const store = useStore();
	const { emailAddress, hrefTFN, tollFreeNumber } = useApplicationData();
  const currentstep = ref<number>(1);

  const timeline = computed(() => {
    return store.state.applicationStatusModule.timeline;
  });

  const stepCount = computed<number | undefined>(() => timeline.value.length || undefined);

  const currentActiveStep = computed<number>(() => {
    return timeline.value.find((s: TimelineMilestone) => s.isActive)?.step || 0;
  });

  const selectedStep = computed<TimelineMilestone>(() => {
    return timeline.value.find((s: TimelineMilestone) => s.step === currentstep.value) || {};
  });

  const applicationStatusTypeId = computed<number>(
    () => store.state.appData.application.applicationStatusId,
  );

  const productTypeId = computed<number>(() => store.state.appData.application.productTypeId);

  const useV2 = computed<boolean>(() => {
    const includedProducts = [ProductIds.PEL, ProductIds.LOC];
    return includedProducts.includes(productTypeId.value);
  });

  const loan = computed(() => store.getters["fundsModule/Loan"]);

  const allowEditing = ref<boolean | undefined>(undefined);

  const lenderUrl = computed<string>(() => {
    return store.state.lenderData && store.state.lenderData.websiteUrl
      ? store.state.lenderData.websiteUrl
      : "https://www.cuselect.com/";
  });

  const emailAddressLinkHtml = computed<string>(() => {
    return ` <a class="inline-link" aria-description="link to email support team" :href="\`mailto:${emailAddress.value}\`">here</a>`;
  });

  function setCurrentStep(step: number) {
    currentstep.value = step;
    store.dispatch("applicationStatusModule/setSelectedStep", step);
  }

  watch(
    () => store.state.applicationStatusModule.selectedStep,
    newValue => {
      setCurrentStep(newValue);
    },
    {
      immediate: true,
    },
  );

  watch(
    () => currentActiveStep.value,
    () => {
      setCurrentStep(currentActiveStep.value);
    },
  );

  return {
    allowEditing,
    applicationStatusTypeId,
    currentActiveStep,
    currentstep,
    emailAddress,
		emailAddressLinkHtml,
    hrefTFN,
    lenderUrl,
    loan,
    productTypeId,
    selectedStep,
    stepCount,
    setCurrentStep,
    timeline,
    tollFreeNumber,
    useV2,
  };
}
